import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { useCheckout } from 'context/checkout/Checkout';
import establishmentsApi from 'api/requests/establishments';
import checkoutApi from 'api/requests/checkout';
import promotionalCodesApi from 'api/requests/promotionalCodes';
import auth from 'middlewares/auth';
import LoginModal from 'modules/onboarding/sign-in/LoginModal';
import PaymentForm from 'modules/checkout/payment/Payment';
import FreePaymentForm from 'modules/checkout/payment/FreePayment';
import CheckoutCollapse from 'modules/events/page/collapse/CheckoutCollapse';
import Modal from 'components/_UI/Modal';
import Text from 'components/_UI/Text';
import Button from 'components/_UI/Button';
import Loader from 'components/_UI/Loader';
import Fieldset from 'components/_UI/Fieldset';
import { Column } from 'styled/alignment/Column';
import { FullyCentered } from 'styled/alignment/Center';
import currency from 'helpers/currency';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import routeParams from 'theme/routeParams';
import buttonText from 'texts/buttons/buttonText';
import { cartDetails, trackEvent } from 'scripts/pixels';
import { getAttributionPayload } from 'scripts/attribution';
import { resolveOrderError } from 'modules/checkout/commom/orderErrors';
import { reservationEndsAt } from 'modules/checkout/commom/reservation';
import { Check, Tag, X } from 'lucide-react';
import ProductCard from './ProductCard';
import StickyCartBar from './StickyCartBar';
import OrderConfirmation from './OrderConfirmation';

export default function ProductOnlyOrder({ establishment, event }) {
    const { checkout, setCheckout } = useCheckout();
    const [searchParams, setSearchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [error, setError] = useState(false);
    const [errorMinOrder, setErrorMinOrder] = useState('');
    const [createOrderError, setCreateOrderError] = useState('');
    const [componentToRender, setComponentToRender] = useState(false);
    const [isPix, setIsPix] = useState(false);
    const [pixToRender, setPixToRender] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [orderId, setOrderId] = useState(null);
    const [isCouponVisible, setIsCouponVisible] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [couponId, setCouponId] = useState('');
    const [promotionalCode, setPromotionalCode] = useState('');
    const [typeDiscount, setTypeDiscount] = useState('');
    const [errorCode, setErrorCode] = useState(false);
    const [couponLoading, setCouponLoading] = useState(false);

    useEffect(() => {
        if (establishment?.slug) {
            fetchProducts();
        }
    }, [establishment?.slug]);

    async function fetchProducts() {
        setLoading(true);
        try {
            const { data } = await establishmentsApi.getProducts(establishment.slug);
            if (data?.data) {
                setProducts(
                    data.data
                        .filter(product => product?.batches?.[0]?.quantity_available > 0 && Boolean(product?.batches?.[0]?.is_active))
                        .map(product => ({
                            ...product,
                            batches: product.batches.map(batch => ({
                                ...batch,
                                original_price: batch?.original_price || batch.price
                            }))
                        }))
                );
            }
        } catch (err) {
            // Products failed to load
        } finally {
            setLoading(false);
        }
    }

    const getCoupon = async (code) => {
        setCouponLoading(true);
        if (errorCode) setErrorCode(false);
        try {
            const data = await promotionalCodesApi.getCoupon(event?.slug || establishment?.slug, code);
            setTypeDiscount(data.data.data.total_discount);
            setCouponCode(code);
            setCouponId(data.data.data.id || code);
        } catch (err) {
            setErrorCode(true);
        } finally {
            setCouponLoading(false);
        }
    };

    const removeCoupon = () => {
        setCouponCode('');
        setCouponId('');
        setTypeDiscount('');
        setPromotionalCode('');
    };

    const toggleCoupon = () => {
        if (!auth.getToken()) {
            setShowLoginModal(true);
            return;
        }
        setIsCouponVisible(!isCouponVisible);
        setErrorCode(false);
    };

    const getQuantity = (productId) => quantities[productId] || 0;

    const updateCheckoutBatches = useCallback((newQuantities) => {
        const batches = products.reduce((acc, product) => {
            const qty = newQuantities[product.id] || 0;
            if (qty > 0) {
                const batch = product.batches.find(b => b.is_active) || product.batches[0];
                acc.push({ id: batch.id, quantity: qty });
            }
            return acc;
        }, []);

        setCheckout(prev => ({ ...prev, batches }));
    }, [products, setCheckout]);

    const handleIncrement = (product) => {
        if (!auth.getToken()) {
            setShowLoginModal(true);
            return;
        }

        const batch = product.batches.find(b => b.is_active) || product.batches[0];
        const currentQty = getQuantity(product.id);
        const maxQty = Math.min(15, product.quantity || batch.quantity_available);

        if (currentQty >= maxQty) return;

        const newQuantities = { ...quantities, [product.id]: currentQty + 1 };
        setQuantities(newQuantities);
        updateCheckoutBatches(newQuantities);
    };

    const handleDecrement = (product) => {
        const currentQty = getQuantity(product.id);
        if (currentQty <= 0) return;

        const newQuantities = { ...quantities, [product.id]: currentQty - 1 };
        setQuantities(newQuantities);
        updateCheckoutBatches(newQuantities);
    };

    const calculateTotals = () => {
        let totalPrice = 0;
        let originalPrice = 0;
        let itemCount = 0;

        products.forEach(product => {
            const qty = getQuantity(product.id);
            if (qty > 0) {
                const batch = product.batches.find(b => b.is_active) || product.batches[0];
                totalPrice += batch.price * qty;
                originalPrice += Number(batch.original_price) * qty;
                itemCount += qty;
            }
        });

        return { totalPrice, originalPrice, itemCount };
    };

    const { totalPrice, originalPrice, itemCount } = calculateTotals();

    async function handleCheckout() {
        if (!auth.getToken()) {
            setShowLoginModal(true);
            return;
        }

        const payload = { ...checkout };

        if (establishment) {
            payload.establishment_id = establishment.id;
        }

        if (event) {
            payload.event_id = event.id;
        }

        if (couponId) {
            payload.coupon_id = couponId;
        }

        // A campanha que trouxe o comprador viaja com o pedido: é o que permite
        // o relatório do painel e a Conversions API creditarem a venda depois.
        const attribution = getAttributionPayload();

        if (attribution) {
            payload.attribution = attribution;
        }

        if (totalPrice < 5 && totalPrice !== 0) {
            setErrorMinOrder('Valor mínimo do pedido é de 5 reais!');
            return;
        }

        if (!payload?.batches?.length) {
            setError(true);
            return;
        }

        setLoading(true);

        try {
            trackEvent('InitiateCheckout', {
                value: totalPrice,
                ...cartDetails(payload.batches),
            });
            const { data } = await checkoutApi.createOrder(payload);

            const createdOrderId = data.id;
            setOrderId(createdOrderId);
            setCheckout({ ...checkout, orderId: createdOrderId, reservationEndsAt: reservationEndsAt(data) });
            setSearchParams({ 'checkout': 1 });

            if (Number(totalPrice) === 0) {
                setComponentToRender(() => (
                    <StyledCheckoutOrder>
                        <FreePaymentForm
                            tickets={[]}
                            event={event}
                        />
                    </StyledCheckoutOrder>
                ));
                setTimeout(() => onPaymentSuccess(createdOrderId), 500);
            } else {
                setComponentToRender(() => (
                    <StyledCheckoutOrder>
                        <PaymentForm
                            event={event}
                            tickets={[]}
                            onOrderUnavailable={onOrderUnavailable}
                            onPixTrigger={(pixComponent) => renderPix(pixComponent)}
                            onPaymentSuccess={() => onPaymentSuccess(createdOrderId)}
                            skipSuccessModal={window.innerWidth < 1200}
                        />
                    </StyledCheckoutOrder>
                ));
            }
        } catch ({ response }) {
            if (response?.status === 401) {
                setShowLoginModal(true);
                return;
            }

            handleOrderError(response);
        } finally {
            setLoading(false);
        }
    }

    /**
     * Toda recusa do pedido precisa virar texto na tela. Quando o motivo é
     * estoque velho, os produtos também são relidos — sem isso o comprador
     * continua olhando uma quantidade que não existe mais e tenta de novo.
     */
    function handleOrderError(response) {
        const { message, shouldRefreshStock } = resolveOrderError(response);

        if (!shouldRefreshStock) {
            setCreateOrderError(message);
            return;
        }

        setCreateOrderError(`${message} Atualizando os itens disponíveis.`);

        setTimeout(() => {
            setCreateOrderError('');
            setCheckout({});
            setQuantities({});
            fetchProducts();
        }, 1500);
    }

    function onPaymentSuccess(successOrderId) {
        const isMobile = window.innerWidth < 1200;

        if (successOrderId) {
            setOrderId(successOrderId);
        }

        setSearchParams((prevSearchParams) => {
            const newSearchParams = new URLSearchParams(prevSearchParams);
            newSearchParams.delete(routeParams.payments.checkout);
            return newSearchParams;
        });
        setComponentToRender(false);
        setIsPix(false);
        setQuantities({});
        setCheckout({});
        fetchProducts();

        if (isMobile) {
            setPaymentSuccess(true);
        }
    }

    function renderPix(PixComponent) {
        setIsPix(true);
        setPixToRender(() => (<PixComponent />));
    }

    function onCloseCollapse() {
        setCheckout({});
        setQuantities({});
        setComponentToRender(false);
        setIsPix(false);
        setSearchParams((prevSearchParams) => {
            const newSearchParams = new URLSearchParams(prevSearchParams);
            newSearchParams.delete(routeParams.payments.checkout);
            return newSearchParams;
        });
        fetchProducts();
    }

    // O pedido deixou de valer (reserva vencida ou já pago): volta à lista e diz o porquê.
    function onOrderUnavailable(message) {
        onCloseCollapse();
        setCreateOrderError(message);
    }

    function closeConfirmation() {
        setPaymentSuccess(false);
        setOrderId(null);
    }

    if (loading) {
        return (
            <FullyCentered height='300px'>
                <Loader />
            </FullyCentered>
        );
    }

    return (
        <>
            <Container>
                <Text
                    type='bold'
                    textAlign='center'
                    color={colors.occasionalPurple}
                    marginTop='8px'
                >
                    Produtos
                </Text>

                <Text textAlign='center' color={colors.night} paddingBottom={0}>
                    Compre agora e retire no evento.
                </Text>

                <CouponContainer>
                    {isCouponVisible ? (
                        <CouponBox hasError={errorCode} hasCode={!!couponCode}>
                            <CouponInputRow>
                                {couponCode ? (
                                    <AppliedCode>
                                        <AppliedCodeContent>
                                            <Check color={colors.successDark} size={18} />
                                            <Text color={colors.successDark} marginLeft='5px' paddingBottom={0}>
                                                {couponCode}
                                            </Text>
                                            {typeDiscount && (
                                                <DiscountBadge>
                                                    <Text color={colors.successDark} paddingBottom={0} name='small'>
                                                        {typeDiscount} OFF
                                                    </Text>
                                                </DiscountBadge>
                                            )}
                                        </AppliedCodeContent>
                                        <X color={colors.nightDark} size={16} onClick={removeCoupon} style={{ cursor: 'pointer' }} />
                                    </AppliedCode>
                                ) : (
                                    <CouponFieldRow>
                                        <Fieldset
                                            placeholder='Digite seu código'
                                            layout='squired'
                                            onChangeValue={e => setPromotionalCode(e)}
                                        />
                                        {couponLoading ? (
                                            <Loader />
                                        ) : (
                                            <ApplyButton onClick={() => getCoupon(promotionalCode)}>
                                                {buttonText.apply}
                                            </ApplyButton>
                                        )}
                                    </CouponFieldRow>
                                )}
                            </CouponInputRow>
                            {errorCode && (
                                <Text color={colors.danger} name='small' paddingBottom={0} marginTop='8px'>
                                    Código inválido!
                                </Text>
                            )}
                        </CouponBox>
                    ) : (
                        <CouponToggle onClick={toggleCoupon}>
                            <Tag width={14} color={colors.success} />
                            <CouponToggleText>Tem um cupom?</CouponToggleText>
                        </CouponToggle>
                    )}
                </CouponContainer>

                {products.length > 0 ? (
                    <ProductListContainer>
                        {products.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                quantity={getQuantity(product.id)}
                                onIncrement={() => handleIncrement(product)}
                                onDecrement={() => handleDecrement(product)}
                            />
                        ))}
                    </ProductListContainer>
                ) : (
                    <FullyCentered height='100px'>
                        <Text color={colors.night} textAlign='center'>
                            Nenhum produto disponível no momento.
                        </Text>
                    </FullyCentered>
                )}

                <DesktopCheckout>
                    {itemCount > 0 ? (
                        <>
                            <DesktopCheckoutInfo>
                                <Text color={colors.nightDark} type='bold' paddingBottom={0}>
                                    {itemCount === 1 ? '1 item' : `${itemCount} itens`}
                                </Text>
                                <DesktopCheckoutDot />
                                <Text color={colors.nightDark} type='bold' paddingBottom={0} name='highlight'>
                                    {currency.format(totalPrice)}
                                </Text>
                            </DesktopCheckoutInfo>
                            <DesktopCheckoutButton onClick={handleCheckout}>
                                Fazer pedido
                            </DesktopCheckoutButton>
                        </>
                    ) : (
                        <DesktopCheckoutEmpty>
                            <Text color={colors.nightLight} paddingBottom={0}>
                                Adicione itens ao seu pedido
                            </Text>
                            <DesktopCheckoutButton disabled>
                                Fazer pedido
                            </DesktopCheckoutButton>
                        </DesktopCheckoutEmpty>
                    )}
                </DesktopCheckout>

                {errorMinOrder && (
                    <FullyCentered>
                        <Text color={colors.danger} name='small'>{errorMinOrder}</Text>
                    </FullyCentered>
                )}

                {createOrderError && (
                    <FullyCentered>
                        <Text color={colors.danger} name='small'>{createOrderError}</Text>
                    </FullyCentered>
                )}
            </Container>

            <StickyCartBar
                itemCount={itemCount}
                totalPrice={totalPrice}
                onCheckout={handleCheckout}
            />

            <CheckoutCollapse
                visible={searchParams.get(routeParams.payments.checkout) == 1 && !!componentToRender}
                onClose={onCloseCollapse}
            >
                {!isPix ? componentToRender : pixToRender}
            </CheckoutCollapse>

            {showLoginModal && (
                <LoginModal
                    isOpen={showLoginModal}
                    onClose={() => setShowLoginModal(false)}
                />
            )}

            <Modal isOpen={error} onClose={() => setError(false)}>
                <Column verticalCenter>
                    <Text color={colors.danger}>Você deve selecionar pelo menos 1 item!</Text>
                    <ContentButton>
                        <Button onClick={() => setError(false)} marginTop='15px'>
                            {buttonText.understood}
                        </Button>
                    </ContentButton>
                </Column>
            </Modal>

            {paymentSuccess && orderId && (
                <OrderConfirmation orderId={orderId} onClose={closeConfirmation} />
            )}
        </>
    );
}

const StyledCheckoutOrder = styled(Column)`
    height: 100%;
`;

const Container = styled(Column)`
    width: 100%;
    padding-bottom: 80px;

    @media screen and (min-width: ${breakpoints.lg}) {
        min-height: calc(100vh - 300px);
        padding-bottom: 20px;
    }
`;

const ProductListContainer = styled.div`
    width: 100%;
    padding: 8px 12px;
    display: flex;
    flex-direction: column;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;

        & > div + div {
            margin-top: 0;
        }
    }
`;

const ContentButton = styled.div`
    width: 270px;
`;

const CouponContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 6px;
    margin-bottom: 4px;
    padding: 0 12px;
`;

const CouponToggle = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    padding: 4px 0;
`;

const CouponToggleText = styled.span`
    font-size: 13px;
    color: ${colors.success};
    font-weight: 600;
    text-decoration: underline;
    text-decoration-style: dashed;
    text-underline-offset: 2px;
`;

const CouponBox = styled.div`
    width: 100%;
    max-width: 400px;
    padding: 12px;
    border: ${({ hasError }) => hasError ? `2px solid ${colors.danger}` : `1px solid ${colors.success}`};
    border-radius: 8px;
    background: ${({ hasCode }) => hasCode ? '#f0fdf4' : 'none'};
`;

const CouponInputRow = styled.div`
    margin-top: 10px;
`;

const CouponFieldRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    & > div {
        flex: 1;
        padding-bottom: 0;

        & > input {
            margin-top: 0;
        }
    }
`;

const ApplyButton = styled.button`
    width: 100px;
    height: 37px;
    background: ${colors.occasionalPurple};
    border-radius: 6px;
    cursor: pointer;
    color: ${colors.neutralLight};
    border: none;
    font-weight: 600;
    flex-shrink: 0;
`;

const AppliedCode = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 10px;
    border: 1px solid #ccc;
    background: ${colors.neutralLight};
    border-radius: 6px;
    height: 37px;
`;

const AppliedCodeContent = styled.div`
    display: flex;
    align-items: center;
`;

const DiscountBadge = styled.div`
    background: #f0fdf4;
    padding: 2px 6px;
    margin-left: 8px;
`;

const DesktopCheckout = styled.div`
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: auto;
        padding: 16px 20px;
        background: ${colors.neutralDark};
        border-radius: 12px;
        margin-left: 12px;
        margin-right: 12px;
    }
`;

const DesktopCheckoutInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const DesktopCheckoutDot = styled.div`
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${colors.nightLight};
`;

const DesktopCheckoutEmpty = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const DesktopCheckoutButton = styled.button`
    padding: 12px 32px;
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s ease;
    opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        opacity: ${({ disabled }) => (disabled ? 0.4 : 0.9)};
    }
`;
